/**
 * Workflow Validation Types
 * Types for client-side validation of workflow nodes, edges, and graph structure
 */

import { WorkflowNodeType } from './workflow.types'

/**
 * Severity level for validation issues
 */
export enum ValidationSeverity {
  Error = 'error',
  Warning = 'warning',
}

/**
 * Categories of validation issues for grouping in UI
 */
export enum ValidationCategory {
  Connectivity = 'connectivity',
  Configuration = 'configuration',
  GraphStructure = 'graph_structure',
}

/**
 * Individual validation issue with node tracking
 */
export interface ValidationIssue {
  id: string                        // Unique ID for React keys
  nodeId: string | null             // Associated node ID (null for graph-level issues)
  nodeLabel?: string                // Human-readable node label
  nodeType?: WorkflowNodeType       // Node type for context
  severity: ValidationSeverity
  category: ValidationCategory
  code: ValidationErrorCode         // Machine-readable error code
  message: string                   // Human-readable message
  details?: string                  // Additional context
  handleId?: string                 // Specific handle that has the issue
}

/**
 * Per-node validation state for UI highlighting
 */
export interface NodeValidationState {
  nodeId: string
  hasErrors: boolean
  hasWarnings: boolean
  errorCount: number
  warningCount: number
  issues: ValidationIssue[]
}

/**
 * Graph analysis results
 */
export interface GraphAnalysisResult {
  hasUnreachableNodes: boolean
  unreachableNodeIds: string[]
  hasCycles: boolean
  cycleNodeIds: string[]
  hasOrphanedPaths: boolean
  orphanedPathNodeIds: string[]
}

/**
 * Comprehensive validation result
 */
export interface WorkflowValidationResult {
  isValid: boolean
  timestamp: Date

  // Aggregated counts
  errorCount: number
  warningCount: number

  // All issues (flat list)
  errors: ValidationIssue[]
  warnings: ValidationIssue[]

  // Per-node validation states (for node highlighting)
  nodeStates: Map<string, NodeValidationState>

  // Graph analysis results
  graphAnalysis: GraphAnalysisResult
}

/**
 * Validation error codes for consistent messaging
 */
export const ValidationErrorCodes = {
  // Connectivity errors
  MISSING_TARGET_CONNECTION: 'MISSING_TARGET_CONNECTION',
  MISSING_SOURCE_CONNECTION: 'MISSING_SOURCE_CONNECTION',
  MISSING_HANDLE_CONNECTION: 'MISSING_HANDLE_CONNECTION',
  SELF_CONNECTION: 'SELF_CONNECTION',

  // Challenge configuration errors
  MISSING_QUESTION_TEXT: 'MISSING_QUESTION_TEXT',
  QUESTION_TEXT_TOO_SHORT: 'QUESTION_TEXT_TOO_SHORT',
  MISSING_ANSWER: 'MISSING_ANSWER',
  ANSWER_TOO_SHORT: 'ANSWER_TOO_SHORT',
  NO_ANSWER_SELECTED: 'NO_ANSWER_SELECTED',
  MISSING_QR_CODE: 'MISSING_QR_CODE',
  MISSING_NUMPAD_CODE: 'MISSING_NUMPAD_CODE',

  // Reward configuration errors
  MISSING_POI_LOCATION: 'MISSING_POI_LOCATION',
  INVALID_COORDINATES: 'INVALID_COORDINATES',
  MISSING_MESSAGE: 'MISSING_MESSAGE',
  MESSAGE_TOO_SHORT: 'MESSAGE_TOO_SHORT',
  MISSING_AUDIO_FILE: 'MISSING_AUDIO_FILE',
  MISSING_VIDEO_URL: 'MISSING_VIDEO_URL',
  MISSING_IMAGE: 'MISSING_IMAGE',
  MISSING_ADVENTURE: 'MISSING_ADVENTURE',
  MISSING_GROUP: 'MISSING_GROUP',
  MISSING_COLLECTION: 'MISSING_COLLECTION',
  INVALID_TIME_VALUE: 'INVALID_TIME_VALUE',
  MISSING_GIFT_REWARD_TEMPLATE: 'MISSING_GIFT_REWARD_TEMPLATE',
  MISSING_CLUE_IDS: 'MISSING_CLUE_IDS',

  // Flow configuration errors
  MISSING_CONDITION: 'MISSING_CONDITION',
  MISSING_GEOFENCE_LOCATION: 'MISSING_GEOFENCE_LOCATION',

  // Route configuration errors
  MISSING_ROUTE_SOURCE: 'MISSING_ROUTE_SOURCE',
  MISSING_ROUTE_DESTINATION: 'MISSING_ROUTE_DESTINATION',
  INVALID_ROUTE_SOURCE: 'INVALID_ROUTE_SOURCE',
  INVALID_ROUTE_DESTINATION: 'INVALID_ROUTE_DESTINATION',
  MISSING_ROUTE_POINTS: 'MISSING_ROUTE_POINTS',

  // Graph structure errors
  INFINITE_LOOP: 'INFINITE_LOOP',
  NO_PATH_TO_EXIT: 'NO_PATH_TO_EXIT',
  UNREACHABLE_NODE: 'UNREACHABLE_NODE',
  MULTIPLE_START_NODES: 'MULTIPLE_START_NODES',
  MISSING_START_NODE: 'MISSING_START_NODE',
  MISSING_EXIT_NODE: 'MISSING_EXIT_NODE',
} as const

export type ValidationErrorCode = typeof ValidationErrorCodes[keyof typeof ValidationErrorCodes]
